import { CSSProperties, Fragment, useEffect, useState } from "react";
import useCurrentUser from "Hooks/useCurrentUser";
import { useHistory } from "react-router-dom";
import CardSymphony from "elements/CardSymphony";
import SwitchSymphony from "elements/SwitchSymphony";
import { getCookie, setCookie, styx } from "modules/Utils";
import PageSymphony from "elements/PageSymphony";
import useCurrentBrand, { BrandExistsState } from "Hooks/useCurrentBrand";
import CustomAvatar from "components/ProfileDropdown/CustomAvatar";
import Colors from "modules/Colors";
import ReactJson, { ReactJsonViewProps } from 'react-json-view';
import Sty from "modules/Sty";
import InputSymphony from "elements/InputSymphony";
import ButtonSymphony from "elements/ButtonSymphony";
import { getBrand_api, removeOrAddUserToBrand_api, searchBrands_new_api, updateBrand_api } from "api/brandApi";
import { getUsers_api, updateUser_api } from "api/userApi";
import Loader from "elements/Loader";
import Icon from "elements/Icon";

const JSON_COLLAPSED = 0;

const CustomerSupportBrandEditingTools = () => {
    const history = useHistory();
    const { isSymphonyAdmin } = useCurrentUser();
    const {
        loadedBrands,
        currentBrand,
        getBrandImage
    } = useCurrentBrand() as BrandExistsState

    const [isLoading, setIsLoading] = useState(false);

    const [brandSearchResults, setBrandSearchResults] = useState<any[]>([]);

    const [selectedBrand, setSelectedBrand] = useState(currentBrand);
    const [selectedUser, setSelectedUser] = useState<any>(null);

    const [brandSearchText, setBrandSearchText] = useState('');
    const [brandNameText, setBrandNameText] = useState(currentBrand.name);
    const [brandSlugText, setBrandSlugText] = useState(currentBrand.slug);
    const [userEmailText, setUserEmailText] = useState('');
    const [userEmailForSearchText, setUserEmailForSearchText] = useState('');

    const [userSearchResults, setUserSearchResults] = useState<any[]>([]);

    /** State to track developer mode, persisted in cookies */
    const [isDevMode, setIsDevMode] = useState(() => {
        const devModeCookie = getCookie('devMode');
        return devModeCookie === 'true';
    });

    const [error, setError] = useState('');

    const reactJsonDefaultProps: any = {
        theme: 'monokai',
        style: sty.jsonViewer,
        collapsed: JSON_COLLAPSED,
        enableClipboard: true,
        displayDataTypes: false,
        name: false,
        indentWidth: 6,
    }

    useEffect(() => {
        if (!isSymphonyAdmin) {
            history.push('/');
        }
    }, [isSymphonyAdmin]);

    useEffect(() => {
        setBrandNameText(selectedBrand.name);
        setBrandSlugText(selectedBrand.slug);
    }, [selectedBrand]);

    useEffect(() => {
        setError('');
    }, [userEmailForSearchText, userEmailText, selectedUser, brandNameText, brandSlugText, selectedBrand]);

    const handleDevModeChange = () => {
        const newDevMode = !isDevMode;
        setIsDevMode(newDevMode);
        setCookie('devMode', String(newDevMode));
    };

    const handleSelectBrand = async (brand: any) => {
        if (brand.id === selectedBrand.id || isLoading) {
            return;
        }

        try {
            setIsLoading(true);
            const brandWithUsers = await getBrand_api(brand.id);
            if (!brandWithUsers) {
                setError('Brand not found');
                return;
            }
            setSelectedBrand(brandWithUsers);
        } catch (error) {
            setError('Error fetching brand');
        } finally {
            setIsLoading(false);
        }
    }

    const handleUpdateBrand = async () => {
        const payload = {
            name: brandNameText,
            slug: brandSlugText,
        }
        try {
            setIsLoading(true);
            await updateBrand_api(selectedBrand.id, payload);
        } catch (error) {
            setError('Error updating brand');
        } finally {
            setIsLoading(false);
        }
    }

    const handleUpdateUser = async () => {
        const payload = {
            email: userEmailText,
        }

        if (!selectedUser) {
            setError('No user selected');
            return;
        }

        setIsLoading(true);
        try {
            await updateUser_api(selectedUser.id, payload);
        } catch (error) {
            setError('Error updating user');
        } finally {
            setIsLoading(false);
        }
    }

    const handleAddOrRemoveUserToBrand = async (userId: string) => {
        try {
            setIsLoading(true);
            await removeOrAddUserToBrand_api(selectedBrand.id, userId);
            const brandWithUsers = await getBrand_api(selectedBrand.id);
            if (!brandWithUsers) {
                setError('Brand not found');
                return;
            }
            setSelectedBrand(brandWithUsers);
        } catch (error) {
            setError('Error removing user from brand');
        } finally {
            setIsLoading(false);
        }
    }

    const searchBrands = async () => {
        try {
            setIsLoading(true);
            const searchCriteria = {
                name: brandSearchText,
                slug: brandSearchText,
                brandId: brandSearchText,
            }
            const brands = await searchBrands_new_api(searchCriteria as any);
            setBrandSearchResults(brands);
        } catch (error) {
            setError('Error searching brands');
        } finally {
            setIsLoading(false);
        }
    }

    const getUsers = async () => {
        let users: any[] = [];
        try {
            setIsLoading(true);
            users = await getUsers_api({ email: userEmailForSearchText.trim().toLowerCase() });
        } catch (error) {
            setError('Error fetching users');
            setUserSearchResults([]);
            return;
        } finally {
            setIsLoading(false);
        }

        if (!users) {
            setError('Error fetching users');
            setUserSearchResults([]);
            return;
        }
        if (users.length === 0) {
            setError('No users found');
            setUserSearchResults([]);
            return;
        }
        if (users.length === 1) {
            setSelectedUser(users[0]);
        }
        if (users.length > 1 && !selectedUser) {
            setError('Multiple users found, please select one');
        }
        setUserSearchResults(users);
    }

    const renderBrandSearchResults = () => {
        if (brandSearchResults.length === 0) {
            return null;
        }
        return (
            <div className="adminToolsBrandSelector" style={sty.adminToolsBrandSelector}>
                {brandSearchResults.map((brand, index) => {
                    return (
                        <Fragment key={`${brand.id}-${index}`}>
                            {renderBrandSelectorItemRow(brand)}
                        </Fragment>
                    )
                })}
            </div>
        )
    }

    const renderBrandSelectorItemRow = (brand: any, isGreen?: boolean) => {
        return (
            <div
                className="adminToolsBrandSelectorItem"
                style={styx(
                    sty.brandAdminRow,
                    selectedBrand.id === brand.id ? sty.rowAdminSelected : null,
                    isGreen ? sty.rowAdminGreen : null
                )}
                onClick={() => handleSelectBrand(brand)}
            >
                <div>
                    <CustomAvatar
                        className="adminToolsBrandSelectorItemImage"
                        brandOverride={brand}
                        size={40}
                    />
                </div>
                <div className="adminToolsBrandSelectorItemId">{brand.id}</div>
                <div className="adminToolsBrandSelectorItemName">{brand.name}</div>
                <div className="adminToolsBrandSelectorItemSlug">{brand.slug}</div>
                <div className="adminToolsBrandSelectorItemTier">{brand.tier}</div>
            </div>
        );
    };

    const renderBrandSelector = () => {
        return (
            <div className="adminToolsBrandSelector" style={sty.adminToolsBrandSelector}>
                {loadedBrands.map((brand, index) => {
                    return (
                        <Fragment key={`${brand.id}-${index}`}>
                            {renderBrandSelectorItemRow(brand)}
                        </Fragment>
                    )
                })}
            </div>
        );
    };

    const renderSelectedBrandInfo = () => {
        return (
            <div>
                {renderBrandSelectorItemRow(selectedBrand, true)}
                {selectedBrand.users?.map((user: any) => {
                    return (
                        <div key={user.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div>
                                {user.id}
                            </div>
                            <div>
                                {user.email}
                            </div>
                            <div>
                                {user.name}
                            </div>
                            <Icon
                                className="adminToolsDeleteUser"
                                icon="trash"
                                onClick={() => handleAddOrRemoveUserToBrand(user.id)}
                            />
                        </div>
                    )
                })}
                <br />
                <ReactJson
                    src={selectedBrand}
                    {...reactJsonDefaultProps}
                />

            </div>
        )
    };

    const renderUpdateBrandInfo = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div>Update Brand Info</div>
                <div>
                    <InputSymphony
                        className="adminToolsTextInput"
                        label="Brand Name"
                        value={brandNameText}
                        onChange={(value) => setBrandNameText(value)}
                    />
                    <InputSymphony
                        className="adminToolsTextInput"
                        label="Brand Slug"
                        value={brandSlugText}
                        onChange={(value) => setBrandSlugText(value)}
                    />
                </div>
                <ButtonSymphony
                    className="adminToolsButton"
                    onClick={() => handleUpdateBrand()}
                    isLoading={isLoading}
                >
                    Update Brand Name & Slug
                </ButtonSymphony>
            </div>
        )
    }

    const renderUserSelector = () => {
        if (userSearchResults.length === 0) {
            return null;
        }
        return (
            <div className="adminToolsUserSearchResults" style={sty.adminToolsBrandSelector}>
                {userSearchResults.map((user) => (
                    <div
                        key={user.id}
                        className="adminToolsUserSearchResult"
                        style={styx(
                            sty.adminToolsUserSearchResult,
                            selectedUser?.id === user.id ? sty.rowAdminSelected : null
                        )}
                        onClick={() => {
                            setSelectedUser(user)
                            setUserEmailText(user.email)
                        }}
                    >
                        <div style={Sty.text16grey}>
                            {user.id}
                        </div>
                        <div>
                            {user.email}
                        </div>
                        <div>
                            {user.name}
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    const renderUpdateUserInfo = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div>Update User Info</div>
                <InputSymphony
                    className="adminToolsTextInput"
                    label="User Search By Email"
                    placeholder="Use email to search for user id"
                    value={userEmailForSearchText}
                    onChange={(value) => setUserEmailForSearchText(value)}
                    isSearch
                    onSearch={getUsers}
                    onEnter={getUsers}
                />
                {renderUserSelector()}
                <InputSymphony
                    className="adminToolsTextInput"
                    label="User Email to Change"
                    placeholder="Enter new email"
                    value={userEmailText}
                    onChange={(value) => setUserEmailText(value)}
                />
                <ButtonSymphony
                    className="updateBrandButton"
                    onClick={() => handleUpdateUser()}
                    isLoading={isLoading}
                >
                    Update User Email
                </ButtonSymphony>
                <ButtonSymphony
                    className="addUserToBrandButton"
                    onClick={() => handleAddOrRemoveUserToBrand(selectedUser.id)}
                    isLoading={isLoading}
                >
                    Add User To Selected Brand: {selectedBrand.name}
                </ButtonSymphony>
            </div>
        )
    }

    const renderError = () => {
        if (!error) {
            return null;
        }
        return (
            <div style={{
                backgroundColor: Colors.redTransparent,
                padding: 8,
                borderRadius: 8,
                color: Colors.red,
            }}>
                {error}
            </div>
        )
    }

    return (
        <div className="adminToolsComponent">
            <PageSymphony
                className="adminToolsPage"
                isCard
                isContent
            >
                <div className="adminToolsPageContent" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <CardSymphony
                        className="adminToolsHeader"
                        title="Admin Tools"
                        subtitle="Tools for managing the platform"
                        isNoPadding
                    />
                    <SwitchSymphony
                        className="adminToolsSwitch"
                        checked={isDevMode}
                        onChange={handleDevModeChange}
                        label="Developer Mode"
                        labelPlacement="right"
                    />
                    <InputSymphony
                        className="adminToolsTextInput"
                        label="Brand Search"
                        value={brandSearchText}
                        onChange={(value) => setBrandSearchText(value)}
                        placeholder="Search for a brand by name or slug"
                        isSearch
                        onSearch={searchBrands}
                        onEnter={searchBrands}
                    />
                    {renderBrandSearchResults()}
                    {renderBrandSelector()}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ ...Sty.text18bold, marginTop: 16 }}>
                            Selected Brand
                        </div>
                        <div>
                            {isLoading && <Loader />}
                        </div>
                    </div>

                    {renderSelectedBrandInfo()}
                    {renderUpdateBrandInfo()}
                    {renderUpdateUserInfo()}
                    {renderError()}
                </div>
            </PageSymphony>
        </div>
    );
}

const sty: Record<string, CSSProperties> = {
    brandAdminRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: 10,
        borderColor: Colors.greyLight,
        borderWidth: 1,
        borderRadius: 10,
        cursor: 'pointer',
    },
    adminToolsSelectedBrandInfo: {
        padding: 10,
        border: `1px solid ${Colors.greyLight}`,
        borderRadius: 10,
    },
    adminToolsBrandSelector: {
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 300,
        overflowY: 'auto',
        width: '100%',
        padding: 8,
        borderColor: Colors.greyLight,
        borderWidth: 1,
        borderRadius: 8,
        boxShadow: 'inset 0 0 4px rgba(0, 0, 0, 0.1)',
    },
    adminToolsUserSearchResult: {
        display: 'flex',
        alignItems: 'row',
        gap: 8,
        padding: 8,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: Colors.greyLight,
        cursor: 'pointer',
    },
    rowAdminSelected: {
        backgroundColor: Colors.purpleTransparent,
        borderColor: Colors.purple,
        color: Colors.purple,
        borderWidth: 1,
    },
    rowAdminGreen: {
        backgroundColor: Colors.greenTransparent,
        borderColor: Colors.green,
        color: Colors.green,
        borderWidth: 1,
    }
}

export default CustomerSupportBrandEditingTools;